/**
 * Truncates a millisecond timestamp to the start of the NZ day (Pacific/Auckland midnight).
 *
 * Reduces Typesense facet cardinality from ~N (one per document) to ~D (one per unique day).
 * The output is still a valid millisecond timestamp compatible with dayjs/Date display.
 *
 * Uses date-fns + date-fns-tz for reliable timezone-aware day truncation.
 */
import { startOfDay } from "date-fns"
import { toZonedTime, fromZonedTime } from "date-fns-tz"

const NZ_TIMEZONE = "Pacific/Auckland"

/**
 * Given a millisecond timestamp, returns the millisecond timestamp of midnight NZ time
 * on the same calendar day.
 *
 * @param timestampMs - Unix timestamp in milliseconds
 * @returns Unix timestamp in milliseconds representing 00:00:00.000 NZ time on that day,
 *          or null if input is null/undefined/NaN
 */
export const truncateToNzDay = (
  timestampMs: number | null | undefined,
): number | null => {
  if (timestampMs == null || !Number.isFinite(timestampMs)) return null

  // Convert UTC timestamp to NZ local time
  const nzLocal = toZonedTime(new Date(timestampMs), NZ_TIMEZONE)

  // Truncate to start of day in NZ local time
  const nzMidnight = startOfDay(nzLocal)

  // Convert back to UTC milliseconds
  return fromZonedTime(nzMidnight, NZ_TIMEZONE).getTime()
}
